// src/hooks/useCinematic.js – Particles, screen shake, glow flash
import { useCallback, useEffect } from 'react';

// Module-level singletons (survive re-renders)
let _canvas = null, _ctx = null, _raf = null;
let _ambient = [], _sparks = [];
let _shakeEl = null, _shakeTime = 0, _shakeIntensity = 0;

const COLORS = ['rgba(0,245,255,', 'rgba(124,58,237,', 'rgba(57,255,20,', 'rgba(255,215,0,'];

function mkParticle() {
  return {
    x: Math.random() * (window.innerWidth || 1200),
    y: (window.innerHeight || 800) + 4,
    vx: (Math.random() - 0.5) * 0.32,
    vy: -(Math.random() * 0.42 + 0.12),
    size: Math.random() * 2.1 + 0.4,
    alpha: Math.random() * 0.3 + 0.06,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    age: 0,
    max: Math.random() * 280 + 140,
  };
}

function loop() {
  if (!_canvas || !_ctx) return;
  _ctx.clearRect(0, 0, _canvas.width, _canvas.height);

  // Screen shake
  if (_shakeTime > 0 && _shakeEl) {
    const dx = (Math.random() - 0.5) * _shakeIntensity;
    const dy = (Math.random() - 0.5) * _shakeIntensity;
    _shakeEl.style.transform = `translate(${dx}px,${dy}px)`;
    _shakeTime--;
    _shakeIntensity *= 0.91;
    if (_shakeTime <= 0) { _shakeEl.style.transform = ''; _shakeEl = null; }
  }

  // Ambient particles
  _ambient.forEach((p, i) => {
    p.x += p.vx; p.y += p.vy; p.age++;
    const lr = p.age / p.max;
    const a = lr < 0.1 ? lr / 0.1 : lr > 0.8 ? (1 - lr) / 0.2 : 1;
    _ctx.beginPath();
    _ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
    _ctx.fillStyle = p.color + (p.alpha * a).toFixed(3) + ')';
    _ctx.fill();
    if (p.age >= p.max || p.y < -10) _ambient[i] = mkParticle();
  });

  // Sparks
  for (let i = _sparks.length - 1; i >= 0; i--) {
    const s = _sparks[i];
    s.x += s.vx; s.y += s.vy; s.vy += 0.11; s.vx *= 0.97; s.life -= s.decay;
    if (s.life <= 0) { _sparks.splice(i, 1); continue; }
    _ctx.beginPath();
    _ctx.arc(s.x, s.y, s.size * s.life, 0, Math.PI * 2);
    _ctx.fillStyle = s.color + (s.life * 0.9).toFixed(2) + ')';
    _ctx.fill();
    _ctx.beginPath();
    _ctx.moveTo(s.x, s.y);
    _ctx.lineTo(s.x - s.vx * 3.5, s.y - s.vy * 2.5);
    _ctx.strokeStyle = s.color + (s.life * 0.35).toFixed(2) + ')';
    _ctx.lineWidth = s.size * s.life * 0.5;
    _ctx.stroke();
  }

  _raf = requestAnimationFrame(loop);
}

function ensureCanvas() {
  if (_canvas) return;
  _canvas = document.createElement('canvas');
  _canvas.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:8980;mix-blend-mode:screen;';
  document.body.appendChild(_canvas);
  _ctx = _canvas.getContext('2d');
  const resize = () => { _canvas.width = window.innerWidth; _canvas.height = window.innerHeight; };
  resize();
  window.addEventListener('resize', resize);
  for (let i = 0; i < 38; i++) { const p = mkParticle(); p.y = Math.random() * window.innerHeight; _ambient.push(p); }
  if (!_raf) loop();
}

export function useCinematic() {
  useEffect(() => { ensureCanvas(); }, []);

  const spawnSparks = useCallback((x, y, count, colorRgba) => {
    ensureCanvas();
    const n = count || 16;
    const col = colorRgba || 'rgba(0,245,255,';
    for (let i = 0; i < n; i++) {
      const angle = (Math.PI * 2 * i / n) + (Math.random() - 0.5) * 0.9;
      const spd = Math.random() * 5.5 + 1.8;
      _sparks.push({ x, y, vx: Math.cos(angle)*spd, vy: Math.sin(angle)*spd - Math.random()*2.2, size: Math.random()*3+0.8, life:1, decay: Math.random()*0.032+0.014, color: col });
    }
  }, []);

  const spawnButtonSparks = useCallback((e, hexColor) => {
    const el = e && (e.currentTarget || e.target);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = rect.left + rect.width / 2, y = rect.top + rect.height / 2;
    const hex = hexColor || '#00f5ff';
    let r=0,g=0,b=0;
    if (hex.startsWith('#') && hex.length === 7) {
      r = parseInt(hex.slice(1,3),16); g = parseInt(hex.slice(3,5),16); b = parseInt(hex.slice(5,7),16);
    }
    spawnSparks(x, y, 14, `rgba(${r},${g},${b},`);
  }, [spawnSparks]);

  const screenShake = useCallback((intensity, duration) => {
    _shakeEl = document.getElementById('root') || document.body;
    _shakeIntensity = intensity || 8;
    _shakeTime = duration || 18;
  }, []);

  const glowFlash = useCallback((color, duration) => {
    const el = document.createElement('div');
    const dur = duration || 400;
    el.style.cssText = `position:fixed;inset:0;pointer-events:none;z-index:8999;background:${color||'rgba(0,245,255,0.15)'};opacity:1;transition:opacity ${dur}ms ease;`;
    document.body.appendChild(el);
    requestAnimationFrame(() => requestAnimationFrame(() => { el.style.opacity = '0'; }));
    setTimeout(() => el.remove(), dur + 60);
  }, []);

  return { spawnSparks, spawnButtonSparks, screenShake, glowFlash };
}
